import React, { useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useInvoice } from '@/hooks/useInvoices';
import { Download, X } from 'lucide-react';
import { usePdfFromHtml } from '@/hooks/usePdfFromHtml';
import { useSystemSettings } from '@/hooks/useSystemSettings';
import { useInvoicePlans } from '@/hooks/useInvoicePlans';
import { useDefaultTemplate } from '@/hooks/useMonthlyBilling';
import { useCompanyData } from '@/hooks/use-company-data';
import { useInvoiceCalculations } from '@/hooks/useInvoiceCalculations';
import InvoiceAzure from './InvoiceAzure';
import InvoiceAWS from './InvoiceAWS';
import InvoiceGCP from './InvoiceGCP';
import InvoiceFleet from './InvoiceFleet';

interface InvoiceViewModalProps {
  isOpen: boolean;
  onClose: () => void;
  invoiceId: string;
  autoDownload?: boolean;
  invisible?: boolean;
}

const getTemplateComponent = (templateId: string) => {
  switch (templateId) {
    case 'azure-modern':
      return InvoiceAzure;
    case 'aws-professional':
      return InvoiceAWS;
    case 'gcp-material':
      return InvoiceGCP;
    case 'fleetone-premium':
      return InvoiceFleet;
    default:
      return InvoiceAzure; // Default template
  }
};

export const InvoiceViewModal: React.FC<InvoiceViewModalProps> = ({
  isOpen,
  onClose,
  invoiceId,
  autoDownload = false,
  invisible = false
}) => {
  const { data: invoice, isLoading } = useInvoice(invoiceId);
  const { generatePdfFromElement } = usePdfFromHtml();
  const { data: systemSettings } = useSystemSettings();
  const { defaultTemplate } = useDefaultTemplate();
  
  // Só buscar planos se não houver items no metadata (compatibilidade com invoices antigas)
  const hasMetadataItems = invoice?.metadata?.items && Array.isArray(invoice.metadata.items) && invoice.metadata.items.length > 0;
  const { data: invoicePlans, isLoading: plansLoading } = useInvoicePlans(
    hasMetadataItems ? undefined : invoice?.company_id
  );
  
  const { data: companyData } = useCompanyData(invoice?.company_id);
  const { calculateDueDate } = useInvoiceCalculations();

  // Priorizar items do metadata (novos checkouts) - DEVE estar antes do early return
  const invoiceItems = React.useMemo(() => {
    if (invoice?.metadata?.items && Array.isArray(invoice.metadata.items)) {
      return invoice.metadata.items.map((item: any) => {
        let description = item.name || 'Item sem nome';
        let suffix = '';
        
        // Adicionar sufixo baseado no tipo
        if (item.metadata?.planType === 'system_plan') {
          suffix = ' (Plano de Sistema)';
        } else if (item.metadata?.planType === 'chip_plan') {
          const chipCount = item.quantity || 1;
          suffix = ` (${chipCount} ${chipCount === 1 ? 'chip' : 'chips'})`;
        } else if (item.type === 'product') {
          suffix = ' (Produto)';
        }
        
        return {
          description: description + suffix,
          quantity: item.quantity || 1,
          unit: 'unidade',
          unitPrice: item.price || 0,
          amount: item.price || 0,
          total: (item.price || 0) * (item.quantity || 1)
        };
      });
    }
    
    // Fallback para invoices antigas (usar company_system_plans)
    return (invoicePlans || []).map(plan => ({
      description: `${plan.system_name} - ${plan.plan_name}${plan.is_trial ? ' (Trial)' : ''}`,
      quantity: 1,
      unit: 'unidade',
      unitPrice: plan.price_monthly,
      amount: plan.price_monthly,
      total: plan.price_monthly
    }));
  }, [invoice?.metadata?.items, invoicePlans]);

  // Calculate totals from items
  const subtotal = React.useMemo(() => {
    return invoiceItems.reduce((sum, item) => sum + item.total, 0);
  }, [invoiceItems]);

  // Auto-download when modal loads with invoice data
  useEffect(() => {
    if (autoDownload && invoice && !isLoading && !plansLoading) {
      const timer = setTimeout(async () => {
        await generatePdfFromElement('invoice-a4', {
          filename: `fatura-${invoice.invoice_number || invoice.id}.pdf`,
          mode: 'fit-a4',
          paddingPx: 0,
          background: '#ffffff',
        });
        onClose();
      }, 500); // Small delay to ensure rendering
      return () => clearTimeout(timer);
    }
  }, [autoDownload, invoice, isLoading, plansLoading, generatePdfFromElement, onClose]);

  const handleDownload = async () => {
    if (!invoice) return;
    
    const filename = `fatura-${invoice.invoice_number}.pdf`;
    await generatePdfFromElement('invoice-a4', {
      filename,
      mode: 'fit-a4',
      paddingPx: 0,
      background: '#ffffff',
    });
  };

  if (!invoice || isLoading || plansLoading) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-center py-8">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
              <p className="mt-2 text-muted-foreground">Carregando fatura...</p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  const templateId = invoice.metadata?.template_id || defaultTemplate || 'aws-professional';
  const TemplateComponent = getTemplateComponent(templateId);
  const usesAzureModern = templateId === 'azure-modern';

  // Helper functions for date handling
  const ymd = (date: Date): string => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const formatYmdToPt = (ymdStr: string): string => {
    const [y, m, d] = ymdStr.split('-');
    return `${d}/${m}/${y}`;
  };

  // Calculate proper dates
  const issueYMD = (invoice.created_at?.split('T')[0]) || ymd(new Date());
  const dueYMD = calculateDueDate(issueYMD);
  const issueBR = formatYmdToPt(issueYMD);
  const dueBR = formatYmdToPt(dueYMD);

  const methodNormalized = invoice.payment_method?.toLowerCase() === 'pix' ? 'PIX' : 'Boleto';

  // Map invoice data to template format
  const invoiceData = {
    meta: {
      number: invoice.invoice_number || 'INV-001',
      invoiceNumber: invoice.invoice_number || 'INV-001',
      issueDate: issueBR,
      dueDate: dueBR,
      currency: 'BRL'
    },
    issuer: {
      name: systemSettings?.settingsMap?.company_razao_social || 'Portal ONE Soluções Empresariais',
      document: systemSettings?.settingsMap?.company_cnpj || '12.345.678/0001-00',
      email: systemSettings?.settingsMap?.company_email || 'contato@portalone.adm.br',
      phone: systemSettings?.settingsMap?.company_telefone || '16988096280',
      address: {
        street: systemSettings?.settingsMap?.company_endereco_logradouro || 'Av. Norma Valerio Correa',
        number: systemSettings?.settingsMap?.company_endereco_numero || '946',
        neighborhood: systemSettings?.settingsMap?.company_endereco_bairro || 'Jardim Botanico',
        city: systemSettings?.settingsMap?.company_endereco_cidade || 'Ribeirão Preto',
        state: systemSettings?.settingsMap?.company_endereco_estado || 'São Paulo',
        zip: systemSettings?.settingsMap?.company_endereco_cep || '14021590',
        zipCode: systemSettings?.settingsMap?.company_endereco_cep || '14021590',
        country: 'Brasil',
        complement: systemSettings?.settingsMap?.company_endereco_complemento || 'Sala 52B'
      }
    },
    customer: {
      name: companyData?.name || invoice.company_name || 'Cliente',
      document: companyData?.doc_cnpj || '00.000.000/0001-00',
      email: companyData?.email || 'cliente@empresa.com',
      phone: companyData?.telefone || '(11) 9999-9999',
      address: {
        street: companyData?.endereco_logradouro ? 
          `${companyData.endereco_logradouro}${companyData.endereco_numero ? ', ' + companyData.endereco_numero : ''}` : 
          'Endereço não informado',
        city: companyData?.endereco_cidade || 'Cidade',
        state: companyData?.endereco_estado || 'UF',
        zipCode: companyData?.endereco_cep || '00000-000',
        country: 'Brasil',
        complement: companyData?.endereco_complemento || ''
      }
    },
    items: invoiceItems.length > 0 ? invoiceItems : [
      {
        description: `Assinatura do período ${invoice.period_start} a ${invoice.period_end}`,
        quantity: 1,
        unit: 'unidade',
        unitPrice: invoice.amount_cents / 100,
        amount: invoice.amount_cents / 100,
        total: invoice.amount_cents / 100
      }
    ],
    totals: {
      subtotal: invoiceItems.length > 0 ? subtotal : invoice.amount_cents / 100,
      subTotal: invoiceItems.length > 0 ? subtotal : invoice.amount_cents / 100,
      tax: 0,
      total: invoiceItems.length > 0 ? subtotal : invoice.amount_cents / 100,
      grandTotal: invoiceItems.length > 0 ? subtotal : invoice.amount_cents / 100
    },
    payment: {
      method: methodNormalized,
      due_date: invoice.due_date,
      notes: 'Pagamento via sistema PortalONE',
      pixPayload: invoice.pix_copy_paste,
      pixKey: invoice.metadata?.pix_key,
      boletoLine: invoice.boleto_digitable_line || invoice.metadata?.boleto_digitable_line,
      boletoBarcode: invoice.boleto_barcode || invoice.metadata?.boleto_barcode || ''
    },
    pixQrCodeBase64: invoice.pix_qr_code_base64 || invoice.pix_qr_code,
    boletoUrl: invoice.boleto_url,
    paymentInfo: {
      method: methodNormalized,
      dueDate: dueBR,
      instructions: 'Pagamento via PortalONE',
      boletoBarcode: invoice.boleto_barcode || invoice.metadata?.boleto_barcode || '',
      boletoDigitableLine: invoice.boleto_digitable_line || invoice.metadata?.boleto_digitable_line || ''
    },
    
    // Debug logs
    __debug: (() => {
      console.log('🔍 Invoice Debug:', {
        id: invoice.id,
        invoice_number: invoice.invoice_number,
        boleto_barcode_direct: invoice.boleto_barcode,
        boleto_url: invoice.boleto_url,
        boleto_digitable_line_direct: invoice.boleto_digitable_line,
        metadata_boleto_barcode: invoice.metadata?.boleto_barcode,
        metadata_boleto_digitable_line: invoice.metadata?.boleto_digitable_line,
        metadata_full: invoice.metadata
      });
      return true;
    })(),
    notes: [
      `Fatura gerada automaticamente pelo sistema PortalONE.`,
      `Status: ${invoice.status === 'paid' ? 'Paga' : invoice.status === 'pending' ? 'Pendente' : invoice.status === 'overdue' ? 'Vencida' : 'Cancelada'}`,
      'Para dúvidas entre em contato conosco.'
    ]
  };

  return (
    <Dialog open={isOpen} onOpenChange={invisible ? undefined : onClose}>
      <DialogContent 
        className={invisible ? "fixed -left-[9999px] -top-[9999px] pointer-events-none opacity-0 max-w-4xl" : "max-w-4xl max-h-[90vh] overflow-y-auto"}
      >
        <DialogHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <div>
            <DialogTitle className="text-xl font-semibold">
              Fatura {invoice.invoice_number}
            </DialogTitle>
            <div className="flex items-center gap-2 mt-2">
              <Badge variant={
                invoice.status === 'paid' ? 'default' :
                invoice.status === 'pending' ? 'secondary' : 'destructive'
              }>
                {invoice.status === 'paid' ? 'Paga' :
                 invoice.status === 'pending' ? 'Pendente' :
                 invoice.status === 'overdue' ? 'Vencida' : 'Cancelada'}
              </Badge>
              {invoice.metadata?.manual_payment && (
                <Badge variant="outline" className="text-purple-600 border-purple-600">
                  Baixa Manual
                </Badge>
              )}
            </div>
          </div>
        </DialogHeader>

        <div className="bg-white">
          <TemplateComponent data={invoiceData as any} />
        </div>
      </DialogContent>
    </Dialog>
  );
};